import { supabase } from '@/lib/supabase';
import { toDashboardUiError } from '@/lib/dashboard-errors';
import { isLightViewActionLike } from '@/lib/light-view-action';
import type {
  DashboardExecuteRequest,
  DashboardExecuteResponse,
} from '@shared/types/dashboard-execution';
import type { SubjectActivitySummary } from '@shared/types/activity-spine';
import type { DashboardComposition } from '@shared/types/dashboard-composition';
import type { Recommendation } from '@shared/types/recommendations';
import type { AnyViewEnvelope } from '@shared/types/views';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isEnvelopeLike = (value: unknown): value is AnyViewEnvelope => (
  isRecord(value)
  && typeof value.view_type === 'string'
  && typeof value.schema_version === 'string'
  && isRecord(value.data)
);

const isRecommendationLike = (value: unknown): value is Recommendation => (
  isRecord(value)
  && typeof value.id === 'string'
  && typeof value.kind === 'string'
  && typeof value.priority === 'string'
  && typeof value.title === 'string'
  && typeof value.reason === 'string'
  && (value.explanation === undefined || typeof value.explanation === 'string')
  && isRecord(value.target)
  && value.target.type === 'dashboard_view_instance'
  && typeof value.target.dashboardId === 'string'
  && typeof value.target.viewInstanceId === 'string'
  && typeof value.target.patternKey === 'string'
  && (value.lightAction === undefined || value.lightAction === null || isLightViewActionLike(value.lightAction))
  && Array.isArray(value.sourceViewInstanceIds)
  && value.sourceViewInstanceIds.every((entry) => typeof entry === 'string')
  && typeof value.generatedAt === 'string'
);

const isDashboardCompositionLike = (value: unknown): value is DashboardComposition => (
  isRecord(value)
  && value.version === '1.0'
  && isRecord(value.metadata)
  && isRecord(value.layout)
  && Array.isArray(value.views)
);

const isSubjectActivitySummaryLike = (value: unknown): value is SubjectActivitySummary => (
  isRecord(value)
  && typeof value.subjectKind === 'string'
  && (value.subjectKey === null || typeof value.subjectKey === 'string')
  && typeof value.subjectLabel === 'string'
  && (value.latestEventAt === null || typeof value.latestEventAt === 'string')
  && Array.isArray(value.items)
  && value.items.every((item) => (
    isRecord(item)
    && typeof item.id === 'string'
    && typeof item.actorLabel === 'string'
    && typeof item.eventType === 'string'
    && typeof item.eventAt === 'string'
    && typeof item.sourceKind === 'string'
    && (item.sourceToolKey === null || typeof item.sourceToolKey === 'string')
    && (item.sourceConnector === null || typeof item.sourceConnector === 'string')
    && (item.sourceRunId === null || typeof item.sourceRunId === 'string')
    && (item.outcomeStatus === null || typeof item.outcomeStatus === 'string')
    && (item.outcomeSummary === null || typeof item.outcomeSummary === 'string')
    && (item.outcomeArtifactType === null || typeof item.outcomeArtifactType === 'string')
  ))
);

const isDashboardExecuteResponse = (value: unknown): value is DashboardExecuteResponse => (
  isRecord(value)
  && typeof value.dashboardId === 'string'
  && isDashboardCompositionLike(value.composition)
  && isRecord(value.viewEnvelopesByInstanceId)
  && Object.values(value.viewEnvelopesByInstanceId).every((entry) => entry === null || isEnvelopeLike(entry))
  && Array.isArray(value.recommendations)
  && value.recommendations.every((entry) => isRecommendationLike(entry))
  && (
    value.selectedExecutionActivitySummary === undefined
    || value.selectedExecutionActivitySummary === null
    || isSubjectActivitySummaryLike(value.selectedExecutionActivitySummary)
  )
);

export const invokeDashboardExecute = async (
  request: DashboardExecuteRequest,
): Promise<DashboardExecuteResponse> => {
  const { data, error } = await supabase.functions.invoke('dashboard-execute', {
    body: request,
  });

  if (error) {
    throw await toDashboardUiError(error, 'Impossible d’afficher ce dashboard pour le moment.');
  }

  if (!isDashboardExecuteResponse(data)) {
    throw new Error('Réponse invalide de dashboard-execute.');
  }

  return data;
};
